(function () {
  'use strict';

  var previousRuntime = window.LueurThemeRuntime;
  if (previousRuntime && typeof previousRuntime.destroy === 'function') previousRuntime.destroy();

  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js-enabled');

  var listenerCleanups = [];
  var components = new Map();
  var activeCartDrawer = null;
  var booted = false;
  var carouselSequence = 0;

  function listen(target, eventName, handler, options) {
    target.addEventListener(eventName, handler, options);
    listenerCleanups.push(function () { target.removeEventListener(eventName, handler, options); });
  }

  function readStorage(key, fallback) {
    try {
      var stored = window.localStorage.getItem(key);
      return stored === null ? fallback : stored;
    } catch (error) {
      return fallback;
    }
  }

  function writeStorage(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      return false;
    }
    return true;
  }

  function uniqueStoredStrings(items) {
    var seen = Object.create(null);
    return items.reduce(function (unique, item) {
      if (typeof item !== 'string') return unique;
      var normalized = item.trim();
      var key = normalized.toLowerCase();
      if (!normalized || seen[key]) return unique;
      seen[key] = true;
      unique.push(normalized);
      return unique;
    }, []);
  }

  function readStoredList(key) {
    try {
      var parsed = JSON.parse(readStorage(key, '[]'));
      return Array.isArray(parsed) ? uniqueStoredStrings(parsed) : [];
    } catch (error) {
      return [];
    }
  }

  function routesRoot() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    root = typeof root === 'string' && root.length ? root : '/';
    if (root.charAt(0) !== '/') root = '/' + root;
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root;
  }

  function routePath(path) {
    return routesRoot() + String(path || '').replace(/^\/+/, '');
  }

  function elementsWithin(root, selector) {
    var elements = [];
    if (!root) return elements;
    if (root.nodeType === 1 && root.matches(selector)) elements.push(root);
    if (typeof root.querySelectorAll === 'function') {
      Array.prototype.push.apply(elements, root.querySelectorAll(selector));
    }
    return elements;
  }

  function registerComponent(element, factory) {
    if (components.has(element)) return components.get(element);
    var component = factory(element) || {};
    components.set(element, component);
    return component;
  }

  function cleanupComponent(element) {
    var component = components.get(element);
    if (!component) return;
    components.delete(element);
    if (typeof component.destroy === 'function') component.destroy();
  }

  function cleanupWithin(root) {
    Array.from(components.keys()).forEach(function (element) {
      if (element === root || (root && typeof root.contains === 'function' && root.contains(element))) {
        cleanupComponent(element);
      }
    });
  }

  function closestElement(target, selector) {
    var element = target && target.nodeType === 1 ? target : target && target.parentElement;
    return element && typeof element.closest === 'function' ? element.closest(selector) : null;
  }

  function parseColor(value) {
    value = String(value || '').trim().toLowerCase();
    var rgbMatch = value.match(/^rgba?\(\s*([\d.]+)[, ]+\s*([\d.]+)[, ]+\s*([\d.]+)(?:[, /]+\s*([\d.]+))?\s*\)$/);
    if (rgbMatch) {
      return {
        red: Number(rgbMatch[1]),
        green: Number(rgbMatch[2]),
        blue: Number(rgbMatch[3]),
        alpha: rgbMatch[4] === undefined ? 1 : Number(rgbMatch[4])
      };
    }

    var hex = value.replace(/^#/, '');
    if (/^[0-9a-f]{3}$/.test(hex)) hex = hex.split('').map(function (part) { return part + part; }).join('');
    if (!/^[0-9a-f]{6}$/.test(hex)) return null;
    return {
      red: parseInt(hex.slice(0, 2), 16),
      green: parseInt(hex.slice(2, 4), 16),
      blue: parseInt(hex.slice(4, 6), 16),
      alpha: 1
    };
  }

  function isLightColor(value) {
    var color = parseColor(value);
    if (!color || color.alpha < 0.05) return null;
    return (color.red * 0.299 + color.green * 0.587 + color.blue * 0.114) >= 160;
  }

  function syncHeaderLogo(header) {
    if (!header) return;
    var mode = header.dataset.logoMode || 'auto';
    var useLightLogo = mode === 'light';

    if (mode === 'auto') {
      var styles = window.getComputedStyle(header);
      var backgroundIsLight = isLightColor(styles.backgroundColor);
      if (backgroundIsLight === null && header.classList.contains('site-header--transparent')) {
        var transparentTextIsLight = isLightColor(styles.getPropertyValue('--header-transparent-text'));
        useLightLogo = transparentTextIsLight === null ? true : transparentTextIsLight;
      } else if (backgroundIsLight !== null) {
        useLightLogo = !backgroundIsLight;
      }
    }

    header.classList.toggle('site-header--logo-light', useLightLogo);
  }

  function syncHeaderLogos(root) {
    elementsWithin(root, '.site-header').forEach(syncHeaderLogo);
  }

  function appendSearchResult(list, result, resultType) {
    var item = document.createElement('li');
    var link = document.createElement('a');
    var meta = document.createElement('span');
    link.href = result.url;
    link.textContent = result.title;
    meta.textContent = resultType;
    link.appendChild(meta);
    item.appendChild(link);
    list.appendChild(item);
  }

  function setupSearchDialog(dialog) {
    var state = {
      dialog: dialog,
      input: dialog.querySelector('[data-predictive-search-input]'),
      results: dialog.querySelector('[data-predictive-search-results]'),
      requestTimer: null,
      focusTimer: null,
      controller: null,
      requestId: 0
    };

    state.cancelRequest = function () {
      window.clearTimeout(state.requestTimer);
      state.requestTimer = null;
      state.requestId += 1;
      if (state.controller) state.controller.abort();
      state.controller = null;
    };

    state.destroy = function () {
      state.cancelRequest();
      window.clearTimeout(state.focusTimer);
      dialog.removeEventListener('close', state.cancelRequest);
    };

    dialog.addEventListener('close', state.cancelRequest);

    return state;
  }

  function requestPredictiveSearch(input) {
    var dialog = closestElement(input, '[data-search-drawer]');
    if (!dialog) return;
    var state = components.get(dialog) || registerComponent(dialog, setupSearchDialog);
    if (!state.results) return;

    state.cancelRequest();
    var query = input.value.trim();
    if (query.length < 2) {
      state.results.replaceChildren();
      return;
    }

    var requestId = state.requestId;
    state.requestTimer = window.setTimeout(function () {
      var controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
      var endpoint = new URL(routePath('search/suggest.json'), window.location.origin);
      endpoint.searchParams.set('q', query);
      endpoint.searchParams.set('resources[type]', 'product,article,page');
      endpoint.searchParams.set('resources[limit]', '6');
      state.controller = controller;

      window.fetch(endpoint.toString(), controller ? { signal: controller.signal } : undefined)
        .then(function (response) {
          return response.ok ? response.json() : Promise.reject(new Error('Search request failed'));
        })
        .then(function (payload) {
          if (requestId !== state.requestId || input.value.trim() !== query) return;
          var resources = payload.resources && payload.resources.results;
          var list = document.createElement('ul');
          if (resources) {
            (resources.products || []).forEach(function (result) { appendSearchResult(list, result, 'Product'); });
            (resources.articles || []).forEach(function (result) { appendSearchResult(list, result, 'Article'); });
            (resources.pages || []).forEach(function (result) { appendSearchResult(list, result, 'Page'); });
          }
          state.results.replaceChildren(list);
          if (!list.children.length) state.results.textContent = 'No results found.';
        })
        .catch(function (error) {
          if ((controller && controller.signal.aborted) || (error && error.name === 'AbortError')) return;
          if (requestId === state.requestId) state.results.textContent = '';
        })
        .finally(function () {
          if (requestId === state.requestId) state.controller = null;
        });
    }, 220);
  }

  function setInteractiveVisibility(element, visible, useHidden) {
    if (useHidden) element.hidden = !visible;
    element.setAttribute('aria-hidden', String(!visible));
    if (visible) element.removeAttribute('inert');
    else element.setAttribute('inert', '');
  }

  function addMediaQueryListener(mediaQuery, handler) {
    if (!mediaQuery) return function () {};
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handler);
      return function () { mediaQuery.removeEventListener('change', handler); };
    }
    if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handler);
      return function () { mediaQuery.removeListener(handler); };
    }
    return function () {};
  }

  function setupSlideshow(slideshow) {
    var slides = slideshow.querySelectorAll('.slideshow-section__slide');
    var controls = slideshow.querySelectorAll('[data-slide-to]');
    var pauseButton = slideshow.querySelector('[data-slideshow-pause]');
    var track = slideshow.querySelector('.slideshow-section__track');
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var initial = Array.prototype.findIndex.call(slides, function (slide) {
      return slide.classList.contains('is-active');
    });
    var state = {
      current: initial >= 0 ? initial : 0,
      timer: null,
      autoplay: slides.length > 1 && slideshow.dataset.autoplay === 'true',
      manualPaused: false,
      hoverPaused: false,
      focusPaused: false,
      editorPaused: false,
      editorPauseReasons: {},
      reducedMotionPaused: reducedMotion.matches,
      destroyed: false
    };
    var pauseLabel = pauseButton ? pauseButton.textContent.trim() || 'Pause' : 'Pause';
    var playLabel = pauseButton && pauseButton.dataset.playLabel ? pauseButton.dataset.playLabel : 'Play';

    state.stopTimer = function () {
      window.clearTimeout(state.timer);
      state.timer = null;
    };

    state.shouldPlay = function () {
      return state.autoplay && !state.manualPaused && !state.hoverPaused && !state.focusPaused &&
        !state.editorPaused && !state.reducedMotionPaused && !document.hidden;
    };

    state.syncPauseButton = function () {
      if (!pauseButton) return;
      var userPaused = state.manualPaused || state.reducedMotionPaused;
      pauseButton.setAttribute('aria-pressed', String(userPaused));
      pauseButton.setAttribute('aria-label', userPaused ? 'Play slideshow' : 'Pause slideshow');
      pauseButton.textContent = userPaused ? playLabel : pauseLabel;
    };

    state.schedule = function () {
      state.stopTimer();
      if (!state.shouldPlay()) {
        if (track) track.setAttribute('aria-live', 'polite');
        return;
      }
      if (track) track.setAttribute('aria-live', 'off');
      state.timer = window.setTimeout(function () {
        state.timer = null;
        state.showSlide(state.current + 1, { autoplay: true });
      }, Number(slideshow.dataset.speed) || 5000);
    };

    state.showSlide = function (index, options) {
      if (!slides.length || state.destroyed) return;
      state.current = (index + slides.length) % slides.length;
      Array.prototype.forEach.call(slides, function (slide, slideIndex) {
        var active = slideIndex === state.current;
        slide.classList.toggle('is-active', active);
        setInteractiveVisibility(slide, active, false);
      });
      Array.prototype.forEach.call(controls, function (control) {
        var active = Number(control.dataset.slideTo) === state.current;
        if (active) control.setAttribute('aria-current', 'true');
        else control.removeAttribute('aria-current');
      });
      if (!options || !options.autoplay) state.stopTimer();
      state.schedule();
    };

    state.togglePaused = function () {
      if (!state.autoplay) return;
      if (state.manualPaused || state.reducedMotionPaused) {
        state.manualPaused = false;
        state.reducedMotionPaused = false;
      } else {
        state.manualPaused = true;
      }
      state.syncPauseButton();
      state.schedule();
    };

    state.setEditorPaused = function (paused, reason) {
      state.editorPauseReasons[reason || 'general'] = !!paused;
      state.editorPaused = Object.keys(state.editorPauseReasons).some(function (key) {
        return state.editorPauseReasons[key];
      });
      state.schedule();
    };

    function handlePointerEnter() {
      if (slideshow.dataset.pauseOnHover !== 'true') return;
      state.hoverPaused = true;
      state.schedule();
    }

    function handlePointerLeave() {
      state.hoverPaused = false;
      state.schedule();
    }

    function handleFocusIn() {
      state.focusPaused = true;
      state.schedule();
    }

    function handleFocusOut(event) {
      if (event.relatedTarget && slideshow.contains(event.relatedTarget)) return;
      state.focusPaused = false;
      state.schedule();
    }

    function handleMotionChange(event) {
      state.reducedMotionPaused = event.matches;
      state.syncPauseButton();
      state.schedule();
    }

    function handleVisibilityChange() {
      state.schedule();
    }

    slideshow.addEventListener('pointerenter', handlePointerEnter);
    slideshow.addEventListener('pointerleave', handlePointerLeave);
    slideshow.addEventListener('focusin', handleFocusIn);
    slideshow.addEventListener('focusout', handleFocusOut);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    var removeMotionListener = addMediaQueryListener(reducedMotion, handleMotionChange);

    state.showSlide(state.current);
    state.syncPauseButton();

    state.destroy = function () {
      state.destroyed = true;
      state.stopTimer();
      removeMotionListener();
      slideshow.removeEventListener('pointerenter', handlePointerEnter);
      slideshow.removeEventListener('pointerleave', handlePointerLeave);
      slideshow.removeEventListener('focusin', handleFocusIn);
      slideshow.removeEventListener('focusout', handleFocusOut);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };

    return state;
  }

  function createCarouselControls(track, label, includePause) {
    var generatedId = !track.id;
    if (generatedId) track.id = 'LueurCarousel-' + (++carouselSequence);

    var controls = document.createElement('div');
    controls.className = 'lueur-carousel-controls';
    controls.dataset.runtimeCarouselControls = '';
    controls.setAttribute('aria-label', label + ' controls');
    controls.style.display = 'flex';
    controls.style.alignItems = 'center';
    controls.style.justifyContent = 'flex-end';
    controls.style.gap = '0.5rem';
    controls.style.marginBlockEnd = '1rem';

    var previous = document.createElement('button');
    previous.type = 'button';
    previous.dataset.carouselPrevious = '';
    previous.setAttribute('aria-controls', track.id);
    previous.textContent = 'Previous';

    var next = document.createElement('button');
    next.type = 'button';
    next.dataset.carouselNext = '';
    next.setAttribute('aria-controls', track.id);
    next.textContent = 'Next';

    var status = document.createElement('span');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.style.position = 'absolute';
    status.style.width = '1px';
    status.style.height = '1px';
    status.style.padding = '0';
    status.style.margin = '-1px';
    status.style.overflow = 'hidden';
    status.style.clip = 'rect(0, 0, 0, 0)';
    status.style.whiteSpace = 'nowrap';
    status.style.border = '0';

    controls.appendChild(previous);
    controls.appendChild(next);
    var pause = null;
    if (includePause) {
      pause = document.createElement('button');
      pause.type = 'button';
      pause.dataset.carouselPause = '';
      pause.setAttribute('aria-controls', track.id);
      pause.setAttribute('aria-pressed', 'false');
      pause.textContent = 'Pause';
      controls.appendChild(pause);
    }
    controls.appendChild(status);

    return {
      element: controls,
      previous: previous,
      next: next,
      pause: pause,
      status: status,
      generatedId: generatedId
    };
  }

  function setupPagedCarousel(track, items, options) {
    var desktopQuery = window.matchMedia('(min-width: 750px)');
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var originalRoleDescription = track.getAttribute('aria-roledescription');
    var originalLive = track.getAttribute('aria-live');
    var controls = createCarouselControls(track, options.label, !!options.autoplay);
    var state = {
      track: track,
      page: 0,
      pageSize: 1,
      timer: null,
      autoplay: !!options.autoplay,
      manualPaused: false,
      hoverPaused: false,
      focusPaused: false,
      editorPaused: false,
      editorPauseReasons: {},
      reducedMotionPaused: reducedMotion.matches,
      destroyed: false
    };
    controls.element.lueurCarouselState = state;

    track.setAttribute('aria-roledescription', 'carousel');
    track.setAttribute('aria-live', 'polite');
    track.parentNode.insertBefore(controls.element, track);

    Array.prototype.forEach.call(items, function (item, index) {
      item.setAttribute('aria-posinset', String(index + 1));
      item.setAttribute('aria-setsize', String(items.length));
    });

    state.totalPages = function () {
      return Math.max(1, Math.ceil(items.length / state.pageSize));
    };

    state.stopTimer = function () {
      window.clearTimeout(state.timer);
      state.timer = null;
    };

    state.shouldPlay = function () {
      return state.autoplay && state.totalPages() > 1 && !state.manualPaused && !state.hoverPaused &&
        !state.focusPaused && !state.editorPaused && !state.reducedMotionPaused && !document.hidden;
    };

    state.syncPauseButton = function () {
      if (!controls.pause) return;
      var paused = state.manualPaused || state.reducedMotionPaused;
      controls.pause.setAttribute('aria-pressed', String(paused));
      controls.pause.setAttribute('aria-label', paused ? 'Play carousel' : 'Pause carousel');
      controls.pause.textContent = paused ? 'Play' : 'Pause';
    };

    state.schedule = function () {
      state.stopTimer();
      if (!state.shouldPlay()) return;
      state.timer = window.setTimeout(function () {
        state.timer = null;
        var nextPage = state.page + 1;
        if (nextPage >= state.totalPages()) nextPage = 0;
        state.showPage(nextPage, { autoplay: true });
      }, Number(options.speed) || 7000);
    };

    state.showPage = function (page, eventOptions) {
      if (state.destroyed) return;
      var totalPages = state.totalPages();
      state.page = Math.max(0, Math.min(Number(page) || 0, totalPages - 1));
      var first = state.page * state.pageSize;
      var last = Math.min(first + state.pageSize, items.length);

      Array.prototype.forEach.call(items, function (item, index) {
        setInteractiveVisibility(item, index >= first && index < last, true);
      });

      controls.previous.disabled = state.page === 0;
      controls.next.disabled = state.page >= totalPages - 1;
      controls.previous.setAttribute('aria-disabled', String(controls.previous.disabled));
      controls.next.setAttribute('aria-disabled', String(controls.next.disabled));
      controls.element.hidden = totalPages <= 1;
      controls.status.textContent = 'Page ' + (state.page + 1) + ' of ' + totalPages;
      if (!eventOptions || !eventOptions.autoplay) state.stopTimer();
      state.schedule();
    };

    state.updatePageSize = function () {
      var firstVisibleIndex = state.page * state.pageSize;
      state.pageSize = desktopQuery.matches ? options.desktopColumns : options.mobileColumns;
      state.pageSize = Math.max(1, Number(state.pageSize) || 1);
      state.page = Math.floor(firstVisibleIndex / state.pageSize);
      state.showPage(state.page);
    };

    state.previous = function () { state.showPage(state.page - 1); };
    state.next = function () { state.showPage(state.page + 1); };
    state.togglePaused = function () {
      if (!state.autoplay) return;
      if (state.manualPaused || state.reducedMotionPaused) {
        state.manualPaused = false;
        state.reducedMotionPaused = false;
      } else {
        state.manualPaused = true;
      }
      state.syncPauseButton();
      state.schedule();
    };
    state.showItem = function (item) {
      var index = Array.prototype.indexOf.call(items, item);
      if (index >= 0) state.showPage(Math.floor(index / state.pageSize));
    };
    state.setEditorPaused = function (paused, reason) {
      state.editorPauseReasons[reason || 'general'] = !!paused;
      state.editorPaused = Object.keys(state.editorPauseReasons).some(function (key) {
        return state.editorPauseReasons[key];
      });
      state.schedule();
    };

    function handlePointerEnter() {
      state.hoverPaused = true;
      state.schedule();
    }

    function handlePointerLeave() {
      state.hoverPaused = false;
      state.schedule();
    }

    function handleFocusIn() {
      state.focusPaused = true;
      state.schedule();
    }

    function handleFocusOut(event) {
      if (event.relatedTarget && track.parentNode.contains(event.relatedTarget)) return;
      state.focusPaused = false;
      state.schedule();
    }

    function handleMotionChange(event) {
      state.reducedMotionPaused = event.matches;
      state.syncPauseButton();
      state.schedule();
    }

    function handleVisibilityChange() { state.schedule(); }

    track.parentNode.addEventListener('pointerenter', handlePointerEnter);
    track.parentNode.addEventListener('pointerleave', handlePointerLeave);
    track.parentNode.addEventListener('focusin', handleFocusIn);
    track.parentNode.addEventListener('focusout', handleFocusOut);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    var removeDesktopListener = addMediaQueryListener(desktopQuery, state.updatePageSize);
    var removeMotionListener = addMediaQueryListener(reducedMotion, handleMotionChange);

    state.updatePageSize();
    state.syncPauseButton();

    state.destroy = function () {
      state.destroyed = true;
      state.stopTimer();
      removeDesktopListener();
      removeMotionListener();
      track.parentNode.removeEventListener('pointerenter', handlePointerEnter);
      track.parentNode.removeEventListener('pointerleave', handlePointerLeave);
      track.parentNode.removeEventListener('focusin', handleFocusIn);
      track.parentNode.removeEventListener('focusout', handleFocusOut);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      controls.element.lueurCarouselState = null;
      controls.element.remove();
      if (controls.generatedId) track.removeAttribute('id');
      if (originalRoleDescription === null) track.removeAttribute('aria-roledescription');
      else track.setAttribute('aria-roledescription', originalRoleDescription);
      if (originalLive === null) track.removeAttribute('aria-live');
      else track.setAttribute('aria-live', originalLive);
      Array.prototype.forEach.call(items, function (item) {
        item.hidden = false;
        item.removeAttribute('aria-hidden');
        item.removeAttribute('aria-posinset');
        item.removeAttribute('aria-setsize');
        item.removeAttribute('inert');
      });
    };

    return state;
  }

  function setupFeaturedCollections(section) {
    var state = { carousels: [] };
    if (section.dataset.layout === 'carousel') {
      section.querySelectorAll('.featured-collections-section__group').forEach(function (group, index) {
        var track = group.querySelector('.product-grid');
        var items = track && track.querySelectorAll('.product-card');
        if (!track || !items || !items.length) return;
        state.carousels.push(setupPagedCarousel(track, items, {
          label: 'Featured collection ' + (index + 1),
          desktopColumns: Number(section.dataset.columnsDesktop) || 4,
          mobileColumns: Number(section.dataset.columnsMobile) || 2,
          autoplay: false
        }));
      });
    }
    state.destroy = function () {
      state.carousels.forEach(function (carousel) { carousel.destroy(); });
      state.carousels = [];
    };
    return state;
  }

  function setupTestimonials(section) {
    var state = { carousel: null };
    if (section.dataset.layout === 'carousel') {
      var track = section.querySelector('.testimonials-section__grid');
      var items = track && track.querySelectorAll('figure');
      if (track && items && items.length) {
        state.carousel = setupPagedCarousel(track, items, {
          label: section.getAttribute('aria-label') || 'Testimonials',
          desktopColumns: Number(section.dataset.columnsDesktop) || 3,
          mobileColumns: Number(section.dataset.columnsMobile) || 1,
          autoplay: section.dataset.autoplay === 'true',
          speed: Number(section.dataset.speed) || 7000
        });
      }
    }
    state.showItem = function (item) {
      if (state.carousel) state.carousel.showItem(item);
    };
    state.setEditorPaused = function (paused, reason) {
      if (state.carousel) state.carousel.setEditorPaused(paused, reason);
    };
    state.destroy = function () {
      if (state.carousel) state.carousel.destroy();
      state.carousel = null;
    };
    return state;
  }

  function setupBackgroundVideo(section) {
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var poster = section.querySelector('.background-video-section__poster');
    var videos = Array.prototype.filter.call(section.querySelectorAll('video'), function (video) {
      return video.closest('.background-video-section') === section;
    });
    var iframes = Array.prototype.filter.call(section.querySelectorAll('iframe'), function (iframe) {
      return iframe.closest('.background-video-section') === section;
    });
    var posterInitiallyHidden = poster ? poster.hidden : false;
    var videoStates = videos.map(function (video) {
      return {
        element: video,
        hidden: video.hidden,
        autoplayAttribute: video.hasAttribute('autoplay'),
        autoplay: video.autoplay,
        ariaHidden: video.getAttribute('aria-hidden'),
        wasPlayingBeforeHidden: false
      };
    });
    var iframeStates = iframes.map(function (iframe) {
      return {
        element: iframe,
        hidden: iframe.hidden,
        src: iframe.getAttribute('src'),
        ariaHidden: iframe.getAttribute('aria-hidden')
      };
    });
    var state = { destroyed: false };

    function restoreAriaHidden(element, value) {
      if (value === null) element.removeAttribute('aria-hidden');
      else element.setAttribute('aria-hidden', value);
    }

    state.applyMotionPreference = function (reason) {
      if (state.destroyed) return;
      var showPoster = reducedMotion.matches && section.dataset.reducedMotion === 'show-poster';
      section.classList.toggle('is-reduced-motion', showPoster);
      if (poster) poster.hidden = showPoster ? false : posterInitiallyHidden;

      videoStates.forEach(function (videoState) {
        var video = videoState.element;
        if (showPoster) {
          video.pause();
          video.autoplay = false;
          video.removeAttribute('autoplay');
          video.hidden = !!poster;
          if (video.hidden) video.setAttribute('aria-hidden', 'true');
        } else {
          video.hidden = videoState.hidden;
          restoreAriaHidden(video, videoState.ariaHidden);
          video.autoplay = videoState.autoplay;
          if (videoState.autoplayAttribute) video.setAttribute('autoplay', '');
          else video.removeAttribute('autoplay');
          var shouldResume = reason !== 'visibility' || videoState.wasPlayingBeforeHidden;
          if (section.dataset.autoplay === 'true' && !document.hidden && shouldResume) {
            var playPromise = video.play();
            if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(function () {});
          }
        }
      });

      iframeStates.forEach(function (iframeState) {
        var iframe = iframeState.element;
        if (showPoster) {
          iframe.hidden = true;
          iframe.setAttribute('aria-hidden', 'true');
          if (iframe.getAttribute('src') !== 'about:blank') iframe.setAttribute('src', 'about:blank');
        } else {
          iframe.hidden = iframeState.hidden;
          restoreAriaHidden(iframe, iframeState.ariaHidden);
          if (iframeState.src !== null && iframe.getAttribute('src') !== iframeState.src) iframe.setAttribute('src', iframeState.src);
        }
      });
    };

    function handleMotionChange() { state.applyMotionPreference('preference'); }
    function handleVisibilityChange() {
      if (document.hidden) {
        videoStates.forEach(function (videoState) {
          videoState.wasPlayingBeforeHidden = !videoState.element.paused && !videoState.element.ended;
          videoState.element.pause();
        });
      } else {
        state.applyMotionPreference('visibility');
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    var removeMotionListener = addMediaQueryListener(reducedMotion, handleMotionChange);
    state.applyMotionPreference('initial');

    state.destroy = function () {
      state.destroyed = true;
      removeMotionListener();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      section.classList.remove('is-reduced-motion');
      if (poster) poster.hidden = posterInitiallyHidden;
      videoStates.forEach(function (videoState) {
        var video = videoState.element;
        video.pause();
        video.hidden = videoState.hidden;
        video.autoplay = videoState.autoplay;
        if (videoState.autoplayAttribute) video.setAttribute('autoplay', '');
        else video.removeAttribute('autoplay');
        restoreAriaHidden(video, videoState.ariaHidden);
      });
      iframeStates.forEach(function (iframeState) {
        var iframe = iframeState.element;
        iframe.hidden = iframeState.hidden;
        restoreAriaHidden(iframe, iframeState.ariaHidden);
        if (iframeState.src !== null) iframe.setAttribute('src', iframeState.src);
      });
    };

    return state;
  }

  function setupPopup(popup) {
    var state = { timer: null };
    var storageKey = popup.dataset.popupStorageKey || 'lueur-popup-dismissed';
    var showOnce = popup.dataset.popupOnce !== 'false';
    if (!showOnce || readStorage(storageKey, 'false') !== 'true') {
      state.timer = window.setTimeout(function () {
        popup.hidden = false;
        state.timer = null;
      }, Number(popup.dataset.popupDelay) || 5000);
    }
    state.dismiss = function () {
      window.clearTimeout(state.timer);
      state.timer = null;
      popup.hidden = true;
      if (showOnce) writeStorage(storageKey, 'true');
    };
    state.destroy = function () {
      window.clearTimeout(state.timer);
      state.timer = null;
    };
    return state;
  }

  function focusableElements(container) {
    return Array.prototype.filter.call(container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ), function (element) {
      return !element.hidden && element.getAttribute('aria-hidden') !== 'true';
    });
  }

  function closeCartDrawer(state, restoreFocus) {
    if (!state) return;
    window.clearTimeout(state.focusTimer);
    state.focusTimer = null;
    state.drawer.hidden = true;
    state.drawer.setAttribute('aria-hidden', 'true');
    if (state.isOpen) document.body.style.overflow = state.bodyOverflow;
    if (state.opener) state.opener.setAttribute('aria-expanded', 'false');
    if (activeCartDrawer === state) activeCartDrawer = null;
    if (restoreFocus !== false && state.opener && document.documentElement.contains(state.opener)) {
      state.opener.focus();
    }
    state.opener = null;
    state.isOpen = false;
  }

  function openCartDrawer(state, opener) {
    if (!state) return;
    if (activeCartDrawer && activeCartDrawer !== state) closeCartDrawer(activeCartDrawer, false);
    if (!state.isOpen) state.bodyOverflow = document.body.style.overflow;
    state.opener = opener || null;
    state.drawer.hidden = false;
    state.drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    if (state.opener) state.opener.setAttribute('aria-expanded', 'true');
    state.isOpen = true;
    activeCartDrawer = state;
    window.clearTimeout(state.focusTimer);
    state.focusTimer = window.setTimeout(function () {
      var focusable = focusableElements(state.drawer);
      (focusable[0] || state.drawer).focus();
    }, 0);
  }

  function setupCartDrawer(drawer) {
    var addedTabIndex = !drawer.hasAttribute('tabindex');
    if (addedTabIndex) drawer.setAttribute('tabindex', '-1');
    drawer.setAttribute('aria-hidden', drawer.hidden ? 'true' : 'false');
    var state = {
      drawer: drawer,
      opener: null,
      bodyOverflow: '',
      focusTimer: null,
      isOpen: false
    };
    state.destroy = function () {
      closeCartDrawer(state, false);
      if (addedTabIndex) drawer.removeAttribute('tabindex');
    };
    return state;
  }

  function productFormErrorBox(form) {
    var box = form.querySelector('[data-product-form-error]');
    if (box) return box;
    box = document.createElement('div');
    box.className = 'product-form__errors';
    box.dataset.productFormError = '';
    box.setAttribute('role', 'alert');
    box.hidden = true;
    form.insertBefore(box, form.firstChild);
    return box;
  }

  function refreshCartChrome(opener) {
    return window.fetch(routePath('cart'), {
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    }).then(function (response) {
      return response.ok ? response.text() : Promise.reject(new Error('Cart refresh failed'));
    }).then(function (markup) {
      var parsedDocument = new DOMParser().parseFromString(markup, 'text/html');
      var incomingDrawer = parsedDocument.querySelector('[data-cart-drawer]');
      var currentDrawer = document.querySelector('[data-cart-drawer]');
      if (!incomingDrawer || !currentDrawer) {
        window.location.assign(routePath('cart'));
        return null;
      }

      closeCartDrawer(components.get(currentDrawer), false);
      cleanupComponent(currentDrawer);
      var replacement = document.importNode(incomingDrawer, true);
      currentDrawer.replaceWith(replacement);
      var incomingCounts = parsedDocument.querySelectorAll('.site-header__cart-count');
      document.querySelectorAll('.site-header__cart-count').forEach(function (count, index) {
        var incomingCount = incomingCounts[index] || incomingCounts[0];
        if (!incomingCount) return;
        count.textContent = incomingCount.textContent;
        var currentLink = count.closest('[data-open-cart-drawer]');
        var incomingLink = incomingCount.closest('[data-open-cart-drawer]');
        if (currentLink && incomingLink && incomingLink.getAttribute('aria-label')) {
          currentLink.setAttribute('aria-label', incomingLink.getAttribute('aria-label'));
        }
      });
      var drawerState = registerComponent(replacement, setupCartDrawer);
      openCartDrawer(drawerState, opener);
      return drawerState;
    });
  }

  function updateCartQuantity(input, quantity) {
    if (!input || input.dataset.cartQuantityPending === 'true') return;

    var item = input.closest('[data-cart-item-key]');
    if (!item || !item.dataset.cartItemKey) return;

    var previousQuantity = Number(input.defaultValue);
    if (!isFinite(previousQuantity)) previousQuantity = Number(input.value) || 0;
    quantity = Math.max(0, Number(quantity) || 0);

    input.value = quantity;
    input.dataset.cartQuantityPending = 'true';
    input.setAttribute('aria-busy', 'true');
    Array.prototype.forEach.call(item.querySelectorAll('[data-quantity-step]'), function (button) {
      button.disabled = true;
    });

    window.fetch(routePath('cart/change.js'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: item.dataset.cartItemKey, quantity: quantity })
    })
      .then(function (response) {
        return response.json().catch(function () { return {}; }).then(function (payload) {
          if (!response.ok) return Promise.reject(new Error(payload.description || payload.message || 'Unable to update the cart.'));
          return payload;
        });
      })
      .then(function () {
        if (input.closest('[data-cart-drawer]')) {
          return refreshCartChrome(document.querySelector('[data-open-cart-drawer]'));
        }
        window.location.reload();
        return null;
      })
      .catch(function () {
        input.value = previousQuantity;
        delete input.dataset.cartQuantityPending;
        input.removeAttribute('aria-busy');
        Array.prototype.forEach.call(item.querySelectorAll('[data-quantity-step]'), function (button) {
          button.disabled = false;
        });
        window.location.reload();
      });
  }

  function submitProductFormAjax(form, state, submitter) {
    if (state.addRequestController) state.addRequestController.abort();
    state.addRequestController = typeof window.AbortController === 'function' ? new window.AbortController() : null;
    var errorBox = productFormErrorBox(form);
    var button = submitter || state.button;
    var originalLabel = button ? button.textContent : '';
    errorBox.hidden = true;
    errorBox.textContent = '';
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    var options = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: new FormData(form)
    };
    if (state.addRequestController) options.signal = state.addRequestController.signal;

    window.fetch(routePath('cart/add.js'), options)
      .then(function (response) {
        return response.json().catch(function () { return {}; }).then(function (payload) {
          if (!response.ok) return Promise.reject(new Error(payload.description || payload.message || 'Unable to add this item to the cart.'));
          return payload;
        });
      })
      .then(function () {
        return refreshCartChrome(button).catch(function () {
          window.location.assign(routePath('cart'));
          return null;
        });
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        errorBox.textContent = error && error.message ? error.message : 'Unable to add this item to the cart.';
        errorBox.hidden = false;
      })
      .finally(function () {
        state.addRequestController = null;
        if (button && document.documentElement.contains(button)) {
          button.disabled = !state.currentVariant || !state.currentVariant.available;
          button.removeAttribute('aria-busy');
          button.textContent = originalLabel;
        }
      });
  }

  function openProductLightbox(state, trigger) {
    if (!state || !state.lightbox || !state.lightboxImage || !trigger) return;
    state.lightboxTrigger = trigger;
    state.lightboxImage.src = trigger.dataset.lightboxSrc || trigger.href || '';
    state.lightboxImage.alt = trigger.dataset.lightboxAlt || '';
    if (typeof state.lightbox.showModal === 'function') state.lightbox.showModal();
    else state.lightbox.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    if (state.lightboxClose) state.lightboxClose.focus();
  }

  function closeProductLightbox(state) {
    if (!state || !state.lightbox) return;
    if (typeof state.lightbox.close === 'function' && state.lightbox.open) state.lightbox.close();
    else state.lightbox.removeAttribute('open');
    document.body.style.overflow = '';
    if (state.lightboxImage) state.lightboxImage.removeAttribute('src');
    if (state.lightboxTrigger && document.documentElement.contains(state.lightboxTrigger)) state.lightboxTrigger.focus();
    state.lightboxTrigger = null;
  }

  function setupProductForm(form) {
    var section = form.closest('[data-product-section]');
    var variantData = form.querySelector('[data-product-variants]');
    var variantInput = form.querySelector('[data-variant-id]');
    var state = {
      form: form,
      section: section,
      variantInput: variantInput,
      currentVariant: null,
      dynamicDisabled: false,
      observer: null,
      addRequestController: null
    };

    if (!section || !variantData || !variantInput) return state;

    try {
      state.variants = JSON.parse(variantData.textContent || '[]');
    } catch (error) {
      state.variants = [];
    }

    state.options = section.querySelectorAll('[data-product-option]');
    state.button = form.querySelector('[data-product-submit]');
    state.availability = section.querySelector('[data-product-availability]');
    state.price = section.querySelector('[data-product-price]');
    state.comparePrice = section.querySelector('[data-product-compare-price]');
    state.sku = section.querySelector('[data-product-sku]');
    state.skuValue = section.querySelector('[data-product-sku-value]');
    state.optionButtons = section.querySelectorAll('[data-option-value]');
    state.optionLabels = section.querySelectorAll('[data-selected-option-label]');
    state.gallery = section.querySelector('[data-product-media-gallery]');
    state.galleryMode = state.gallery ? state.gallery.dataset.galleryMode : 'thumbnails';
    state.mediaItems = section.querySelectorAll('.product-media-gallery__item');
    state.thumbnails = section.querySelectorAll('.product-thumbnail');
    state.galleryDots = section.querySelectorAll('[data-gallery-dot]');
    state.inventory = section.querySelector('[data-product-inventory-status]');
    state.pickupContainer = form.querySelector('[data-product-pickup-container]');
    state.pickupCurrent = form.querySelector('[data-product-pickup-current]');
    state.lightbox = section.querySelector('[data-product-lightbox]');
    state.lightboxImage = state.lightbox && state.lightbox.querySelector('[data-product-lightbox-image]');
    state.lightboxClose = state.lightbox && state.lightbox.querySelector('[data-product-lightbox-close]');
    state.moneyFormat = section.dataset.moneyFormat || '${{amount}}';
    state.dynamicCheckout = form.querySelector('.shopify-payment-button');
    state.dynamicInitiallyHidden = state.dynamicCheckout ? state.dynamicCheckout.hidden : false;
    state.dynamicInitialPointerEvents = state.dynamicCheckout ? state.dynamicCheckout.style.pointerEvents : '';

    state.formatMoney = function (cents) {
      var amount = Number(cents || 0) / 100;
      var token = state.moneyFormat.match(/\{\{\s*(amount(?:_no_decimals|_with_comma_separator|_no_decimals_with_comma_separator)?)\s*\}\}/);
      if (!token) return amount.toFixed(2);
      var value;
      if (token[1] === 'amount_no_decimals') value = Math.round(amount).toString();
      else if (token[1] === 'amount_with_comma_separator') value = amount.toFixed(2).replace('.', ',');
      else if (token[1] === 'amount_no_decimals_with_comma_separator') value = Math.round(amount).toString();
      else value = amount.toFixed(2);
      return state.moneyFormat.replace(token[0], value);
    };

    state.selectedVariant = function () {
      var selected = Array.prototype.map.call(state.options, function (option) { return option.value; });
      return state.variants.find(function (variant) {
        return variant.options.every(function (value, index) { return value === selected[index]; });
      }) || null;
    };

    state.syncOptionUI = function () {
      Array.prototype.forEach.call(state.optionButtons, function (optionButton) {
        var option = state.options[Number(optionButton.dataset.optionIndex)];
        var selected = option && option.value === optionButton.dataset.optionValue;
        optionButton.classList.toggle('is-selected', !!selected);
        optionButton.setAttribute('aria-pressed', String(!!selected));
      });
      Array.prototype.forEach.call(state.optionLabels, function (label) {
        var option = state.options[Number(label.dataset.optionIndex)];
        if (option) label.textContent = option.value;
      });
    };

    state.syncOptionAvailability = function () {
      Array.prototype.forEach.call(state.optionButtons, function (optionButton) {
        var targetIndex = Number(optionButton.dataset.optionIndex);
        var targetValue = optionButton.dataset.optionValue;
        var canPurchase = state.variants.some(function (variant) {
          if (!variant.available || variant.options[targetIndex] !== targetValue) return false;
          return variant.options.every(function (value, index) {
            return index === targetIndex || !state.options[index] || state.options[index].value === value;
          });
        });
        optionButton.disabled = !canPurchase;
        optionButton.dataset.optionAvailable = String(canPurchase);
        optionButton.setAttribute('aria-disabled', String(!canPurchase));
      });
    };

    state.pauseInactiveMedia = function (activeMediaId) {
      Array.prototype.forEach.call(state.mediaItems, function (media) {
        if (String(media.dataset.mediaId) === String(activeMediaId)) return;
        media.querySelectorAll('video').forEach(function (video) { video.pause(); });
        media.querySelectorAll('iframe').forEach(function (frame) {
          if (!frame.contentWindow) return;
          frame.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
          frame.contentWindow.postMessage(JSON.stringify({ method: 'pause' }), '*');
        });
        media.querySelectorAll('model-viewer').forEach(function (model) {
          if (typeof model.pause === 'function') model.pause();
        });
      });
    };

    state.syncActiveMedia = function (mediaId) {
      var activeMedia;
      var keepAllMediaVisible = state.galleryMode === 'grid' || state.galleryMode === 'stacked';
      Array.prototype.forEach.call(state.mediaItems, function (media) {
        var active = String(media.dataset.mediaId) === String(mediaId);
        media.classList.toggle('is-active', active);
        media.hidden = !keepAllMediaVisible && !active;
        if (active) activeMedia = media;
      });
      Array.prototype.forEach.call(state.thumbnails, function (thumbnail) {
        var active = String(thumbnail.dataset.mediaId) === String(mediaId);
        thumbnail.classList.toggle('is-active', active);
        if (active) thumbnail.setAttribute('aria-current', 'true');
        else thumbnail.removeAttribute('aria-current');
      });
      Array.prototype.forEach.call(state.galleryDots, function (dot) {
        var active = String(dot.dataset.mediaId) === String(mediaId);
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-pressed', String(active));
      });
      state.pauseInactiveMedia(mediaId);
      return activeMedia;
    };

    state.activateMedia = function (mediaId, shouldScroll) {
      if (!mediaId) return;
      var activeMedia = state.syncActiveMedia(mediaId);
      if (shouldScroll && activeMedia) activeMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    };

    state.syncInventory = function (variant) {
      if (!state.inventory) return;
      var threshold = Number(state.inventory.dataset.inventoryThreshold || 0);
      var quantity = Number(variant && variant.inventory_quantity || 0);
      var showLow = !!variant && variant.available && !!variant.inventory_management && quantity > 0 && quantity <= threshold;
      state.inventory.hidden = !showLow;
      if (showLow) state.inventory.textContent = (state.inventory.dataset.lowInventoryLabel || 'Only [quantity] left').replace('[quantity]', quantity);
    };

    state.syncPickupAvailability = function (variant) {
      if (!state.pickupContainer || !state.pickupCurrent) return;
      var template = variant && state.pickupContainer.querySelector('[data-pickup-variant="' + variant.id + '"]');
      state.pickupCurrent.innerHTML = template ? template.innerHTML : '';
    };

    state.setDynamicCheckoutDisabled = function (disabled) {
      state.dynamicDisabled = !!disabled;
      if (!state.dynamicCheckout) return;
      state.dynamicCheckout.setAttribute('aria-disabled', String(state.dynamicDisabled));
      state.dynamicCheckout.hidden = state.dynamicDisabled || state.dynamicInitiallyHidden;
      state.dynamicCheckout.style.pointerEvents = state.dynamicDisabled ? 'none' : state.dynamicInitialPointerEvents;
      state.dynamicCheckout.querySelectorAll('button').forEach(function (dynamicButton) {
        dynamicButton.disabled = state.dynamicDisabled;
        dynamicButton.setAttribute('aria-disabled', String(state.dynamicDisabled));
      });
    };

    state.updateHistory = function (variant) {
      if (!window.history || typeof window.history.replaceState !== 'function') return;
      var productUrl = new URL(window.location.href);
      var sectionUrl = new URL(section.dataset.productUrl || window.location.pathname, window.location.origin);
      if (sectionUrl.pathname !== productUrl.pathname) productUrl.pathname = sectionUrl.pathname;
      if (variant) productUrl.searchParams.set('variant', variant.id);
      else productUrl.searchParams.delete('variant');
      window.history.replaceState({}, '', productUrl.pathname + productUrl.search + productUrl.hash);
    };

    state.updateVariant = function (shouldUpdateHistory) {
      state.syncOptionUI();
      state.syncOptionAvailability();
      var variant = state.selectedVariant();
      state.currentVariant = variant;

      if (!variant) {
        variantInput.value = '';
        variantInput.disabled = true;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        if (state.button) {
          state.button.disabled = true;
          state.button.textContent = state.button.dataset.unavailableLabel || 'Unavailable';
        }
        state.setDynamicCheckoutDisabled(true);
        if (state.availability) {
          state.availability.textContent = state.availability.dataset.unavailableLabel || 'Unavailable';
          state.availability.classList.add('product-availability--sold-out');
        }
        if (state.sku && state.skuValue) {
          state.sku.hidden = true;
          state.skuValue.textContent = '';
        }
        if (state.price) {
          state.price.textContent = '';
          state.price.classList.remove('price--sale');
        }
        if (state.comparePrice) {
          state.comparePrice.hidden = true;
          state.comparePrice.textContent = '';
        }
        state.syncInventory(null);
        state.syncPickupAvailability(null);
        if (shouldUpdateHistory !== false) state.updateHistory(null);
        section.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: null } }));
        return;
      }

      variantInput.disabled = false;
      variantInput.value = variant.id;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      if (state.button) {
        state.button.disabled = !variant.available;
        state.button.textContent = variant.available
          ? (state.button.dataset.availableLabel || 'Add to cart')
          : (state.button.dataset.soldOutLabel || 'Sold out');
      }
      state.setDynamicCheckoutDisabled(!variant.available);
      if (state.availability) {
        state.availability.textContent = variant.available
          ? (state.availability.dataset.availableLabel || 'In stock')
          : (state.availability.dataset.soldOutLabel || 'Sold out');
        state.availability.classList.toggle('product-availability--sold-out', !variant.available);
      }
      if (state.price) {
        state.price.dataset.price = variant.price;
        state.price.textContent = state.formatMoney(variant.price);
        state.price.classList.toggle('price--sale', variant.compare_at_price > variant.price);
      }
      if (state.comparePrice) {
        var showComparePrice = variant.compare_at_price > variant.price;
        state.comparePrice.hidden = !showComparePrice;
        state.comparePrice.dataset.price = variant.compare_at_price || 0;
        state.comparePrice.textContent = showComparePrice ? state.formatMoney(variant.compare_at_price) : '';
      }
      if (state.sku && state.skuValue) {
        state.sku.hidden = !variant.sku;
        state.skuValue.textContent = variant.sku || '';
      }
      state.syncInventory(variant);
      state.syncPickupAvailability(variant);
      if (variant.featured_media && variant.featured_media.id) state.activateMedia(variant.featured_media.id, true);
      if (shouldUpdateHistory !== false) state.updateHistory(variant);
      section.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: variant } }));
    };

    if (state.dynamicCheckout && typeof window.MutationObserver === 'function') {
      state.observer = new window.MutationObserver(function () {
        state.setDynamicCheckoutDisabled(state.dynamicDisabled);
      });
      state.observer.observe(state.dynamicCheckout, { childList: true, subtree: true });
    }

    if (state.gallery && typeof window.IntersectionObserver === 'function' && window.matchMedia('(max-width: 999px)').matches) {
      state.mediaObserver = new window.IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) state.syncActiveMedia(entry.target.dataset.mediaId);
        });
      }, { root: state.gallery, threshold: [0.6] });
      Array.prototype.forEach.call(state.mediaItems, function (media) { state.mediaObserver.observe(media); });
    }

    if (state.lightbox) {
      state.onLightboxClose = function () {
        document.body.style.overflow = '';
        if (state.lightboxTrigger && document.documentElement.contains(state.lightboxTrigger)) state.lightboxTrigger.focus();
        state.lightboxTrigger = null;
      };
      state.lightbox.addEventListener('close', state.onLightboxClose);
    }

    state.destroy = function () {
      if (state.observer) state.observer.disconnect();
      if (state.mediaObserver) state.mediaObserver.disconnect();
      if (state.lightbox && state.onLightboxClose) state.lightbox.removeEventListener('close', state.onLightboxClose);
      if (state.addRequestController) state.addRequestController.abort();
    };

    state.updateVariant(false);
    return state;
  }

  function productStateFor(node) {
    var section = closestElement(node, '[data-product-section]');
    var form = section && section.querySelector('[data-product-form]');
    return form ? (components.get(form) || registerComponent(form, setupProductForm)) : null;
  }

  function extractProductCard(markup) {
    var parsedDocument = new DOMParser().parseFromString(markup, 'text/html');
    var card = parsedDocument.querySelector('.product-card');
    return card ? {
      handle: card.dataset.productHandle || '',
      html: card.outerHTML
    } : null;
  }

  function uniqueProductCards(cards) {
    var seen = Object.create(null);
    return cards.reduce(function (unique, card) {
      if (!card || !card.html) return unique;
      var key = String(card.handle || card.html).trim().toLowerCase();
      if (!key || seen[key]) return unique;
      seen[key] = true;
      unique.push(card);
      return unique;
    }, []);
  }

  function setupRecentlyViewed(section) {
    var grid = section.querySelector('[data-recently-viewed-grid]');
    var currentHandle = section.dataset.currentProductHandle;
    var limit = Math.max(1, Number(section.dataset.limit) || 4);
    var viewed = readStoredList('lueur-recently-viewed').filter(function (handle) {
      return handle !== currentHandle;
    });
    var controllers = [];
    var state = {};

    if (grid && viewed.length) {
      Promise.all(viewed.map(function (handle) {
        var controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
        if (controller) controllers.push(controller);
        return window.fetch(routePath('products/' + encodeURIComponent(handle) + '?view=card'), controller ? { signal: controller.signal } : undefined)
          .then(function (response) { return response.ok ? response.text().then(extractProductCard) : null; });
      })).then(function (cards) {
        if (!document.documentElement.contains(section)) return;
        var html = uniqueProductCards(cards).slice(0, limit).map(function (card) {
          return card.html;
        }).join('');
        if (!html) return;
        grid.innerHTML = html;
        section.hidden = false;
      }).catch(function (error) {
        if (error && error.name === 'AbortError') return;
        section.hidden = true;
      });
    }

    state.destroy = function () {
      controllers.forEach(function (controller) { controller.abort(); });
    };
    return state;
  }

  function setupProductRecommendations(section) {
    var sectionId = section.dataset.sectionId;
    var productId = section.dataset.productId;
    var limit = section.dataset.limit || '4';
    var controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
    var state = {};

    if (sectionId && productId) {
      var recommendationsUrl = new URL(section.dataset.url || routePath('recommendations/products'), window.location.origin);
      recommendationsUrl.searchParams.set('section_id', sectionId);
      recommendationsUrl.searchParams.set('product_id', productId);
      recommendationsUrl.searchParams.set('limit', limit);
      recommendationsUrl.searchParams.set('intent', section.dataset.intent || 'related');

      window.fetch(recommendationsUrl.toString(), controller ? { signal: controller.signal } : undefined)
        .then(function (response) { return response.ok ? response.text() : ''; })
        .then(function (markup) {
          if (!document.documentElement.contains(section)) return;
          var parsedDocument = new DOMParser().parseFromString(markup, 'text/html');
          var replacement = parsedDocument.querySelector('[data-product-recommendations]');
          cleanupComponent(section);
          if (replacement && replacement.querySelector('.product-card')) section.replaceWith(replacement);
          else section.remove();
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          if (document.documentElement.contains(section)) {
            cleanupComponent(section);
            section.remove();
          }
        });
    }

    state.destroy = function () {
      if (controller) controller.abort();
    };
    return state;
  }

  function setupShippingEstimator(estimator) {
    var country = estimator.querySelector('[name="country"]');
    var province = estimator.querySelector('[name="province"]');
    var provinceWrapper = estimator.querySelector('[data-shipping-province-wrapper]');
    var postalCode = estimator.querySelector('[name="zip"]');
    var submit = estimator.querySelector('[data-shipping-estimator-submit]');
    var errorBox = estimator.querySelector('[data-shipping-estimator-error]');
    var results = estimator.querySelector('[data-shipping-estimator-results]');
    var controller = null;
    var originalButtonLabel = submit ? submit.textContent : '';
    var state = {};

    function parseProvinces(option) {
      if (!option) return [];
      try {
        var parsed = JSON.parse(option.dataset.provinces || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    }

    function selectDefaultCountry() {
      if (!country || !country.dataset.default) return;
      var expected = country.dataset.default.toLowerCase();
      var matchingOption = Array.prototype.find.call(country.options, function (option) {
        return option.value.toLowerCase() === expected || option.textContent.trim().toLowerCase() === expected;
      });
      if (matchingOption) country.value = matchingOption.value;
    }

    function updateProvinces() {
      if (!country || !province || !provinceWrapper) return;
      var selectedProvince = province.value;
      var provinces = parseProvinces(country.options[country.selectedIndex]);
      province.replaceChildren();
      provinces.forEach(function (entry) {
        var option = document.createElement('option');
        option.value = Array.isArray(entry) ? entry[0] : entry;
        option.textContent = Array.isArray(entry) ? entry[1] : entry;
        province.appendChild(option);
      });
      provinceWrapper.hidden = provinces.length === 0;
      province.disabled = provinces.length === 0;
      if (selectedProvince && Array.prototype.some.call(province.options, function (option) { return option.value === selectedProvince; })) {
        province.value = selectedProvince;
      }
    }

    function formatShippingPrice(rate) {
      var numericPrice = Number(rate.price);
      var currency = rate.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active);
      if (Number.isFinite(numericPrice) && currency && typeof window.Intl === 'object') {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || undefined, {
            style: 'currency',
            currency: currency
          }).format(numericPrice);
        } catch (error) {
          return rate.price;
        }
      }
      return rate.price || '';
    }

    function renderError(message) {
      if (results) results.replaceChildren();
      if (!errorBox) return;
      errorBox.textContent = message || estimator.dataset.errorLabel || 'We could not calculate shipping for this address.';
      errorBox.hidden = false;
    }

    function renderRates(rates) {
      if (errorBox) {
        errorBox.textContent = '';
        errorBox.hidden = true;
      }
      if (!results) return;
      results.replaceChildren();
      if (!rates.length) {
        results.textContent = estimator.dataset.noRatesLabel || 'No shipping rates were found for this address.';
        return;
      }
      var list = document.createElement('ul');
      rates.forEach(function (rate) {
        var item = document.createElement('li');
        var name = document.createElement('span');
        var price = document.createElement('strong');
        name.textContent = rate.name || '';
        price.textContent = formatShippingPrice(rate);
        item.append(name, price);
        list.appendChild(item);
      });
      results.appendChild(list);
    }

    function normalizeErrors(payload) {
      if (!payload) return '';
      var messages = [];
      Object.keys(payload).forEach(function (key) {
        var value = payload[key];
        if (Array.isArray(value)) messages = messages.concat(value);
        else if (value) messages.push(value);
      });
      return messages.join(' ');
    }

    function estimate() {
      if (!country || !submit) return;
      if (controller) controller.abort();
      controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      if (errorBox) errorBox.hidden = true;
      if (results) results.replaceChildren();

      var endpoint = new URL(routePath('cart/shipping_rates.json'), window.location.origin);
      endpoint.searchParams.set('shipping_address[country]', country.value);
      if (province && !province.disabled && province.value) endpoint.searchParams.set('shipping_address[province]', province.value);
      if (postalCode && postalCode.value.trim()) endpoint.searchParams.set('shipping_address[zip]', postalCode.value.trim());

      var options = { headers: { Accept: 'application/json' } };
      if (controller) options.signal = controller.signal;
      window.fetch(endpoint.toString(), options)
        .then(function (response) {
          return response.json().catch(function () { return {}; }).then(function (payload) {
            if (!response.ok) return Promise.reject(new Error(normalizeErrors(payload) || estimator.dataset.errorLabel));
            return payload;
          });
        })
        .then(function (payload) { renderRates(payload.shipping_rates || []); })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          renderError(error && error.message);
        })
        .finally(function () {
          controller = null;
          submit.disabled = false;
          submit.removeAttribute('aria-busy');
          submit.textContent = originalButtonLabel;
        });
    }

    selectDefaultCountry();
    updateProvinces();
    if (country) country.addEventListener('change', updateProvinces);
    if (submit) submit.addEventListener('click', estimate);
    state.destroy = function () {
      if (controller) controller.abort();
      if (country) country.removeEventListener('change', updateProvinces);
      if (submit) submit.removeEventListener('click', estimate);
    };
    return state;
  }

  function syncCollectionLayout(container) {
    if (!container) return;
    var grid = container.querySelector('[data-collection-grid]');
    if (!grid) return;
    container.querySelectorAll('[data-collection-layout]').forEach(function (button) {
      var active = grid.dataset.layout === button.dataset.collectionLayout;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  }

  function rememberRecentlyViewed(root) {
    elementsWithin(root, '[data-product-section][data-current-product-handle]').forEach(function (productSection) {
      var handle = productSection.dataset.currentProductHandle;
      if (!handle) return;
      var viewed = readStoredList('lueur-recently-viewed');
      viewed = [handle].concat(viewed.filter(function (item) { return item !== handle; })).slice(0, 8);
      writeStorage('lueur-recently-viewed', JSON.stringify(viewed));
    });
  }

  function setupStickyAnnouncement(bar) {
    if (!bar.matches('[data-sticky-announcement]')) return {};

    var state = {};
    var sectionWrapper = bar.closest('.shopify-section');
    var root = document.documentElement;
    var resizeObserver = null;

    if (sectionWrapper) sectionWrapper.classList.add('shopify-section--sticky-announcement');
    root.classList.add('sticky-announcement-enabled');

    function syncHeight() {
      root.style.setProperty('--announcement-bar-height', Math.ceil(bar.getBoundingClientRect().height) + 'px');
    }

    syncHeight();

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(syncHeight);
      resizeObserver.observe(bar);
    } else {
      window.addEventListener('resize', syncHeight);
    }

    state.destroy = function () {
      if (resizeObserver) resizeObserver.disconnect();
      else window.removeEventListener('resize', syncHeight);
      if (sectionWrapper) sectionWrapper.classList.remove('shopify-section--sticky-announcement');
      root.classList.remove('sticky-announcement-enabled');
      root.style.removeProperty('--announcement-bar-height');
    };

    return state;
  }

  function setupCollectionFilterDrawer(drawer) {
    var state = {};
    state.drawer = drawer;
    state.summary = drawer.querySelector('summary');
    state.panel = drawer.querySelector('[data-collection-filter]');
    state.closeButton = drawer.querySelector('[data-collection-filter-close]');
    state.focusTimer = 0;

    state.sync = function () {
      var isOpen = drawer.hasAttribute('open');
      document.documentElement.classList.toggle('collection-filter-drawer-open', isOpen);
      if (isOpen && state.closeButton) {
        window.clearTimeout(state.focusTimer);
        state.focusTimer = window.setTimeout(function () { state.closeButton.focus(); }, 0);
      }
    };

    state.close = function (restoreFocus) {
      if (!drawer.hasAttribute('open')) return;
      drawer.removeAttribute('open');
      state.sync();
      if (restoreFocus && state.summary) state.summary.focus();
    };

    drawer.addEventListener('toggle', state.sync);
    state.sync();

    state.destroy = function () {
      window.clearTimeout(state.focusTimer);
      drawer.removeEventListener('toggle', state.sync);
      if (drawer.hasAttribute('open')) document.documentElement.classList.remove('collection-filter-drawer-open');
    };

    return state;
  }

  function initScope(root) {
    syncHeaderLogos(root);
    elementsWithin(root, '[data-sticky-announcement]').forEach(function (bar) {
      registerComponent(bar, setupStickyAnnouncement);
    });
    elementsWithin(root, '[data-search-drawer]').forEach(function (dialog) {
      registerComponent(dialog, setupSearchDialog);
    });
    elementsWithin(root, '[data-slideshow]').forEach(function (slideshow) {
      registerComponent(slideshow, setupSlideshow);
    });
    elementsWithin(root, '.featured-collections-section').forEach(function (section) {
      registerComponent(section, setupFeaturedCollections);
    });
    elementsWithin(root, '.testimonials-section').forEach(function (section) {
      registerComponent(section, setupTestimonials);
    });
    elementsWithin(root, '.background-video-section').forEach(function (section) {
      registerComponent(section, setupBackgroundVideo);
    });
    elementsWithin(root, '[data-popup]').forEach(function (popup) {
      registerComponent(popup, setupPopup);
    });
    elementsWithin(root, '[data-cart-drawer]').forEach(function (drawer) {
      registerComponent(drawer, setupCartDrawer);
    });
    elementsWithin(root, '[data-collection-filter-drawer]').forEach(function (drawer) {
      registerComponent(drawer, setupCollectionFilterDrawer);
    });
    elementsWithin(root, '[data-product-form]').forEach(function (form) {
      registerComponent(form, setupProductForm);
    });
    elementsWithin(root, '[data-recently-viewed]').forEach(function (section) {
      registerComponent(section, setupRecentlyViewed);
    });
    elementsWithin(root, '[data-product-recommendations]').forEach(function (section) {
      registerComponent(section, setupProductRecommendations);
    });
    elementsWithin(root, '[data-shipping-estimator]').forEach(function (estimator) {
      registerComponent(estimator, setupShippingEstimator);
    });
    elementsWithin(root, '.main-collection').forEach(syncCollectionLayout);
    rememberRecentlyViewed(root);
  }

  function handleClick(event) {
    var dynamicCheckout = closestElement(event.target, '.shopify-payment-button');
    if (dynamicCheckout) {
      var dynamicState = productStateFor(dynamicCheckout);
      if (dynamicState && (!dynamicState.currentVariant || !dynamicState.currentVariant.available)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
    }

    var menuToggle = closestElement(event.target, '[data-menu-toggle]');
    if (menuToggle) {
      var menuTarget = document.getElementById(menuToggle.dataset.menuTarget);
      if (menuTarget) {
        var expanded = menuToggle.getAttribute('aria-expanded') === 'true';
        menuToggle.setAttribute('aria-expanded', String(!expanded));
        menuTarget.hidden = expanded;
      }
      return;
    }

    var searchTrigger = closestElement(event.target, '[data-open-search]');
    if (searchTrigger) {
      var header = searchTrigger.closest('header');
      var dialog = (header && header.querySelector('[data-search-drawer]')) || document.querySelector('[data-search-drawer]');
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        var searchState = components.get(dialog) || registerComponent(dialog, setupSearchDialog);
        if (!dialog.open) dialog.showModal();
        window.clearTimeout(searchState.focusTimer);
        searchState.focusTimer = window.setTimeout(function () {
          if (searchState.input) searchState.input.focus();
        }, 0);
      }
      return;
    }

    var searchClose = closestElement(event.target, '[data-close-search]');
    if (searchClose) {
      var searchDialog = searchClose.closest('[data-search-drawer]');
      var closeState = searchDialog && components.get(searchDialog);
      if (closeState) closeState.cancelRequest();
      if (searchDialog && typeof searchDialog.close === 'function') searchDialog.close();
      return;
    }

    var clickedDialog = closestElement(event.target, '[data-search-drawer]');
    if (clickedDialog && event.target === clickedDialog) {
      var backdropState = components.get(clickedDialog);
      if (backdropState) backdropState.cancelRequest();
      clickedDialog.close();
      return;
    }

    var layoutButton = closestElement(event.target, '[data-collection-layout]');
    if (layoutButton) {
      var collection = layoutButton.closest('.main-collection');
      var grid = collection && collection.querySelector('[data-collection-grid]');
      if (grid) {
        grid.dataset.layout = layoutButton.dataset.collectionLayout;
        syncCollectionLayout(collection);
      }
      return;
    }

    var filterDismiss = closestElement(event.target, '[data-collection-filter-close], [data-collection-filter-backdrop]');
    if (filterDismiss) {
      var filterDrawer = filterDismiss.closest('[data-collection-filter-drawer]');
      var filterState = filterDrawer && (components.get(filterDrawer) || registerComponent(filterDrawer, setupCollectionFilterDrawer));
      if (filterState) {
        event.preventDefault();
        filterState.close(true);
      }
      return;
    }

    var slideControl = closestElement(event.target, '[data-slide-to]');
    if (slideControl) {
      var slideshow = slideControl.closest('[data-slideshow]');
      var slideshowState = slideshow && components.get(slideshow);
      if (slideshowState) slideshowState.showSlide(Number(slideControl.dataset.slideTo));
      return;
    }

    var slideshowPause = closestElement(event.target, '[data-slideshow-pause]');
    if (slideshowPause) {
      var pausedSlideshow = slideshowPause.closest('[data-slideshow]');
      var pausedSlideshowState = pausedSlideshow && components.get(pausedSlideshow);
      if (pausedSlideshowState) pausedSlideshowState.togglePaused();
      return;
    }

    var carouselPrevious = closestElement(event.target, '[data-carousel-previous]');
    if (carouselPrevious) {
      var previousControls = carouselPrevious.closest('[data-runtime-carousel-controls]');
      var previousCarouselState = previousControls && previousControls.lueurCarouselState;
      if (previousCarouselState) previousCarouselState.previous();
      return;
    }

    var carouselNext = closestElement(event.target, '[data-carousel-next]');
    if (carouselNext) {
      var nextControls = carouselNext.closest('[data-runtime-carousel-controls]');
      var nextCarouselState = nextControls && nextControls.lueurCarouselState;
      if (nextCarouselState) nextCarouselState.next();
      return;
    }

    var carouselPause = closestElement(event.target, '[data-carousel-pause]');
    if (carouselPause) {
      var pauseControls = carouselPause.closest('[data-runtime-carousel-controls]');
      var pauseCarouselState = pauseControls && pauseControls.lueurCarouselState;
      if (pauseCarouselState) pauseCarouselState.togglePaused();
      return;
    }

    var popupClose = closestElement(event.target, '[data-popup-close]');
    if (popupClose) {
      var popup = popupClose.closest('[data-popup]');
      var popupState = popup && components.get(popup);
      if (popupState) popupState.dismiss();
      return;
    }

    var sidebarTrigger = closestElement(event.target, '[data-open-sidebar]');
    if (sidebarTrigger) {
      var sidebar = document.querySelector('[data-sidebar-menu]');
      if (sidebar) sidebar.hidden = false;
      return;
    }

    var sidebarClose = closestElement(event.target, '[data-sidebar-close]');
    if (sidebarClose) {
      var sidebarMenu = sidebarClose.closest('[data-sidebar-menu]');
      if (sidebarMenu) sidebarMenu.hidden = true;
      return;
    }

    var cartTrigger = closestElement(event.target, '[data-open-cart-drawer]');
    if (cartTrigger && window.matchMedia('(min-width: 901px)').matches) {
      var drawer = document.querySelector('[data-cart-drawer]');
      if (drawer) {
        event.preventDefault();
        openCartDrawer(components.get(drawer) || registerComponent(drawer, setupCartDrawer), cartTrigger);
      }
      return;
    }

    var cartClose = closestElement(event.target, '[data-cart-drawer-close]');
    if (cartClose) {
      var closeDrawer = cartClose.closest('[data-cart-drawer]');
      closeCartDrawer(closeDrawer && components.get(closeDrawer), true);
      return;
    }

    var clickedDrawer = closestElement(event.target, '[data-cart-drawer]');
    if (clickedDrawer && event.target === clickedDrawer) {
      closeCartDrawer(components.get(clickedDrawer), true);
      return;
    }

    var quantityControl = closestElement(event.target, '[data-quantity-step]');
    if (quantityControl) {
      var wrapper = quantityControl.closest('.cart-item__quantity');
      var quantityInput = wrapper && wrapper.querySelector('input');
      if (quantityInput) {
        var value = Number(quantityInput.value) || 0;
        var nextQuantity = Math.max(0, value + (quantityControl.dataset.quantityStep === 'up' ? 1 : -1));
        updateCartQuantity(quantityInput, nextQuantity);
      }
      return;
    }

    var lightboxClose = closestElement(event.target, '[data-product-lightbox-close]');
    if (lightboxClose) {
      closeProductLightbox(productStateFor(lightboxClose));
      return;
    }

    var lightbox = closestElement(event.target, '[data-product-lightbox]');
    if (lightbox && event.target === lightbox) {
      closeProductLightbox(productStateFor(lightbox));
      return;
    }

    var zoomTrigger = closestElement(event.target, '[data-product-zoom]');
    if (zoomTrigger) {
      var zoomState = productStateFor(zoomTrigger);
      if (zoomState && zoomState.lightbox) {
        event.preventDefault();
        openProductLightbox(zoomState, zoomTrigger);
      }
      return;
    }

    var optionButton = closestElement(event.target, '[data-option-value]');
    if (optionButton) {
      if (optionButton.dataset.productUrl) {
        var targetUrl = new URL(optionButton.dataset.productUrl, window.location.origin);
        if (targetUrl.pathname !== window.location.pathname) {
          window.location.assign(targetUrl.toString());
          return;
        }
      }
      var optionState = productStateFor(optionButton);
      var option = optionState && optionState.options[Number(optionButton.dataset.optionIndex)];
      if (option) {
        option.value = optionButton.dataset.optionValue;
        option.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    var thumbnail = closestElement(event.target, '.product-thumbnail');
    if (thumbnail) {
      var thumbnailState = productStateFor(thumbnail);
      if (thumbnailState) {
        event.preventDefault();
        thumbnailState.activateMedia(thumbnail.dataset.mediaId, true);
      }
      return;
    }

    var galleryDot = closestElement(event.target, '[data-gallery-dot]');
    if (galleryDot) {
      var dotState = productStateFor(galleryDot);
      if (dotState) {
        event.preventDefault();
        dotState.activateMedia(galleryDot.dataset.mediaId, true);
      }
    }
  }

  function handleInput(event) {
    var input = closestElement(event.target, '[data-predictive-search-input]');
    if (input) requestPredictiveSearch(input);
  }

  function handleChange(event) {
    var cartQuantityInput = closestElement(event.target, '[data-cart-quantity]');
    if (cartQuantityInput) {
      var quantity = Math.max(0, Number(cartQuantityInput.value) || 0);
      updateCartQuantity(cartQuantityInput, quantity);
      return;
    }

    var sort = closestElement(event.target, '[data-sort-by]');
    if (sort) {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      url.searchParams.delete('page');
      window.location.assign(url.toString());
      return;
    }

    var option = closestElement(event.target, '[data-product-option]');
    if (option) {
      var state = productStateFor(option);
      if (state) state.updateVariant();
    }
  }

  function handleSubmit(event) {
    var productForm = closestElement(event.target, '[data-product-form]');
    if (productForm) {
      var productState = components.get(productForm) || registerComponent(productForm, setupProductForm);
      if (!productState.currentVariant || !productState.currentVariant.available || !productState.variantInput || productState.variantInput.disabled) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (event.submitter && event.submitter.name !== 'add') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      submitProductFormAjax(productForm, productState, event.submitter || productState.button);
      return;
    }

    var discountForm = closestElement(event.target, '[data-discount-form]');
    if (discountForm) {
      var discountInput = discountForm.querySelector('[name="code"]');
      if (!discountInput || !discountInput.value.trim()) return;
      event.preventDefault();
      var redirect = routePath('cart');
      window.location.assign(routePath('discount/' + encodeURIComponent(discountInput.value.trim())) + '?redirect=' + encodeURIComponent(redirect));
    }
  }

  function handleKeydown(event) {
    var openFilterDrawer = document.querySelector('[data-collection-filter-drawer][open]');
    if (openFilterDrawer) {
      var openFilterState = components.get(openFilterDrawer) || registerComponent(openFilterDrawer, setupCollectionFilterDrawer);
      if (event.key === 'Escape') {
        event.preventDefault();
        openFilterState.close(true);
        return;
      }
      if (event.key === 'Tab' && openFilterState.panel) {
        var filterFocusable = focusableElements(openFilterState.panel);
        if (!filterFocusable.length) {
          event.preventDefault();
          openFilterState.panel.focus();
          return;
        }
        var filterFirst = filterFocusable[0];
        var filterLast = filterFocusable[filterFocusable.length - 1];
        if (event.shiftKey && document.activeElement === filterFirst) {
          event.preventDefault();
          filterLast.focus();
        } else if (!event.shiftKey && document.activeElement === filterLast) {
          event.preventDefault();
          filterFirst.focus();
        }
        return;
      }
    }

    if (!activeCartDrawer) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeCartDrawer(activeCartDrawer, true);
      return;
    }
    if (event.key !== 'Tab') return;

    var focusable = focusableElements(activeCartDrawer.drawer);
    if (!focusable.length) {
      event.preventDefault();
      activeCartDrawer.drawer.focus();
      return;
    }
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function handleSectionLoad(event) {
    initScope(event.target);
  }

  function handleSectionUnload(event) {
    cleanupWithin(event.target);
  }

  function setEditorPlaybackWithin(root, paused, reason) {
    elementsWithin(root, '[data-slideshow]').forEach(function (slideshow) {
      var slideshowState = components.get(slideshow);
      if (slideshowState) slideshowState.setEditorPaused(paused, reason);
    });
    elementsWithin(root, '.testimonials-section').forEach(function (testimonials) {
      var testimonialsState = components.get(testimonials);
      if (testimonialsState) testimonialsState.setEditorPaused(paused, reason);
    });
  }

  function handleSectionSelect(event) {
    setEditorPlaybackWithin(event.target, true, 'section');
  }

  function handleSectionDeselect(event) {
    setEditorPlaybackWithin(event.target, false, 'section');
  }

  function handleBlockSelect(event) {
    var slide = closestElement(event.target, '.slideshow-section__slide');
    var slideshow = slide && slide.closest('[data-slideshow]');
    var state = slideshow && components.get(slideshow);
    if (slide && state) {
      var slides = Array.prototype.slice.call(slideshow.querySelectorAll('.slideshow-section__slide'));
      state.setEditorPaused(true, 'block');
      state.showSlide(slides.indexOf(slide));
      return;
    }

    var testimonial = closestElement(event.target, '.testimonials-section figure');
    var testimonials = testimonial && testimonial.closest('.testimonials-section');
    var testimonialsState = testimonials && components.get(testimonials);
    if (testimonial && testimonialsState) {
      testimonialsState.setEditorPaused(true, 'block');
      testimonialsState.showItem(testimonial);
    }
  }

  function handleBlockDeselect(event) {
    var slideshow = closestElement(event.target, '[data-slideshow]');
    var slideshowState = slideshow && components.get(slideshow);
    if (slideshowState) slideshowState.setEditorPaused(false, 'block');

    var testimonials = closestElement(event.target, '.testimonials-section');
    var testimonialsState = testimonials && components.get(testimonials);
    if (testimonialsState) testimonialsState.setEditorPaused(false, 'block');
  }

  function boot() {
    if (booted) return;
    booted = true;
    initScope(document);
  }

  function destroy() {
    cleanupWithin(document);
    listenerCleanups.splice(0).forEach(function (cleanup) { cleanup(); });
    if (window.LueurThemeRuntime && window.LueurThemeRuntime.destroy === destroy) {
      window.LueurThemeRuntime = null;
    }
  }

  listen(document, 'click', handleClick);
  listen(document, 'input', handleInput);
  listen(document, 'change', handleChange);
  listen(document, 'submit', handleSubmit, true);
  listen(document, 'keydown', handleKeydown);
  listen(document, 'shopify:section:load', handleSectionLoad);
  listen(document, 'shopify:section:unload', handleSectionUnload);
  listen(document, 'shopify:section:select', handleSectionSelect);
  listen(document, 'shopify:section:deselect', handleSectionDeselect);
  listen(document, 'shopify:block:select', handleBlockSelect);
  listen(document, 'shopify:block:deselect', handleBlockDeselect);

  if (document.readyState === 'loading') listen(document, 'DOMContentLoaded', boot, { once: true });
  else boot();

  window.LueurThemeRuntime = { destroy: destroy, initScope: initScope };
})();
